<template>
  <div>
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />

    <TableRemote
      v-else
      :items="displayedWorkOrders"
      :title="$t('services.billingLaboratory')"
      isSelectable
      :isSearchable="false"
      showExpand
      hideDefaultFooter
      :isInfinitScroll="true"
      :elementsPerPage="queryParams.perpage"
      :pagination="pagination"
      :headers="tableHeaders"
      :sortableColumns="sortableColumns"
      :centerColumns="['clientNumber', 'status', 'invoicedStatus', 'billingStatus']"
      @sendQueryParams="fetchQuery"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :filters="filters"
          :searchPlaceholder="$t('billing.filter.searchBy')"
          :datesRange="true"
          :filterName="filterName"
          :saveFilter="true"
          @onApplyFilters="handleApplyFilters"
          @onSearch="handleSearch"
        />
      </template>
      <template v-slot:[`item.clientNumber`]="{item}">
        <div>
          <button
            class="pr-2"
            @click="info(item)"
          >
            <v-icon
              size="small"
            >
              info_outline
            </v-icon>
          </button>
          <span>{{ item.clientNumber }}</span>
        </div>
      </template>
      <template v-slot:[`item.status`]="{item}">
        <v-icon
          v-if="(item.status === status.TODO)"
          color="primary"
          class="completionStatusIcon"
        >
          panorama_fish_eye
        </v-icon>
        <v-icon
          v-if="item.status === status.IN_PROGRESS"
          color="primary"
          class="completionStatusIcon"
        >
          timelapse
        </v-icon>
        <v-icon
          v-if="isItemCompleted(item.status)"
          color="primary"
          class="completionStatusIcon"
        >
          check_circle_outline
        </v-icon>
      </template>

      <template v-slot:[`item.billingStatus`]="{item}">
        <v-icon
          class="m-0 p-0"
        >
          {{ getInvoicedStatusIcon(item.billingStatus) }}
        </v-icon>
      </template>
      <template #expanded-item="props">
        <td :colspan="props.headers.length">
          <VTableLocal
            :items="props.item.analysisRequests"
            disablePagination
            :centerColumns="['billable', 'invoiced']"
            :notSortableColumns="['billable', 'invoiced']"
            :headers="sampleHeaders"
          >
            <template v-slot:[`item`]="{ item }">
              <tr>
                <td class="text-start custumeTd">
                  {{ item.requestNumber }}
                </td>
                <td class="text-start w-poNumberBillInvocedType">
                  {{ item.poNumber }}
                </td>
                <td class="text-start w-poNumberBillInvocedType">
                  {{ $t(`sampleTypes.${item.type.charAt(0).toLowerCase()}${item.type.substring(1)}`) }}
                </td>
                <td class="text-start w-comp">
                  {{ item.component.name }}
                </td>
                <td class="text-start w-createdAt">
                  {{ $options.filters.formatDate(item.createdAt, 'L LT') }}
                </td>
                <td class="text-start w-status-">
                  {{ $t(`laboratory.labels.${item.status.toLowerCase()}`) }}
                </td>
                <td class="w-poNumberBillInvocedType">
                  <VCheckbox
                    v-model="item.billable"
                    disabled
                    color="primary"
                    class="m-0 p-0 d-flex justify-center"
                  />
                </td> <td class="w-poNumberBillInvocedType">
                  <VCheckbox
                    v-model="item.invoiced"
                    color="primary"
                    class="m-0 p-0 d-flex justify-center"
                    :disabled="item.invoiced"
                    @input="billingStatusUpdate(item._id, item.invoiced)"
                  />
                </td>
              </tr>
            </template>
          </VTableLocal>
        </td>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import labBillingStatus from '@/constants/labBillingStatus';
import EventBus from '@/utilities/event-bus';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import moment from 'moment';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import VTableLocal from '@/components/shared/content-views/VTableLocal.vue';
import { BILLING_STATUS, NOTHING_INVOICED, FULLY_INVOICED, PARTIALLY_INVOICED } from '@/aquassist-shared/constants/WOLInvoicedStatus';
import AnalysisGroupInfo from './analysisGroupInfo.vue';


export default {
    components: {
        VTableLocal,
        TableRemote,
        VCheckbox,
        SearchWithFilters,
    },
    data() {
        return {
            billing: [],
            loading: false,
            workOrders: [],
            pagination: {},
            queryParams: {
                page: 1,
                perpage: 100,
                search: this.search,
                completionStatus: labBillingStatus.SENT,
                dateFrom: null,
                dateTo: null,
                billable: true,
                notInvoiced: false,
                partiallyInvoiced: false,
                fullyInvoiced: false,
            },
            filterName: 'serviceBillingLaboratory',
            filters: [
                {
                    key: 'status',
                    label: 'billing.filter.status',
                    type: 'select',
                    value: [labBillingStatus.SENT],
                    multiple: true,
                    items: [],
                    datacy: 'billingFilterStatus',
                },
                {
                    key: 'date',
                    label: 'billing.filter.date',
                    type: 'date',
                    value: [],
                    dateKeys: ['startDate', 'endDate'],
                    isRange: true,
                    datacy: 'billingFilterDate',
                },
                {
                    key: 'billable',
                    label: 'billing.filter.billing',
                    type: 'select',
                    value: ['billable'],
                    multiple: true,
                    items: [
                        { text: 'billing.filter.billable', value: 'billable' },
                    ],
                    datacy: 'billingFilterBillable',
                },
                {
                    key: 'processed',
                    label: 'billing.filter.billingStatus',
                    type: 'select',
                    value: BILLING_STATUS,
                    multiple: true,
                    items: [
                        { text: `billing.filter.${NOTHING_INVOICED}`, value: NOTHING_INVOICED },
                        { text: `billing.filter.${PARTIALLY_INVOICED}`, value: PARTIALLY_INVOICED },
                        { text: `billing.filter.${FULLY_INVOICED}`, value: FULLY_INVOICED },
                    ],
                    datacy: 'billingFilterProcessed',
                },
            ],
        };
    },
    computed: {
        completionStatus() {
            return Object.values(labBillingStatus).map(
                status => ({
                    value: status,
                    text: this.$t(`forms.labels.${status}`),
                    isAlreadyTranslated: true,
                }),
            );
        },
        status() {
            const woStatus = {};
            Object.keys(labBillingStatus).forEach(key => {
                woStatus[key] = labBillingStatus[key].toLowerCase();
            });

            return woStatus;
        },
        headers() {
            const mobileHeaders = [
                'clientNumber',
                'siteNumber',
                'status',
                'billingStatus',
            ];

            const headers = [
                'clientNumber',
                'siteNumber',
                'wONumber',
                'wOdate',
                'assignedTo',
                'soNumber',
                'analysisRequestCount',
                'status',
                'billingStatus',
            ];
            return this.$vuetify.breakpoint.mdAndDown ? mobileHeaders : headers;
        },
        tableHeaders() {
            return this.headers.map(header => ({
                text: this.$t(`billing.labels.${header}`),
                value: header,
            }));
        },
        sortableColumns() {
            return this.headers.map(column => this.$options.filters.lowercase(column));
        },
        displayedWorkOrders() {
            return this.workOrders.map(workOrder => ({
                ...workOrder,
                clientNumber: workOrder.client.accountNumber,
                siteNumber: workOrder.site.deliveryAccountNumber,
                assignedTo: this.getAssignedTo(workOrder.assignedTo),
                wONumber: workOrder.woNumber,
                wOdate: workOrder.startDate ? this.$options.filters.formatDate(workOrder.startDate, 'L LT') : null,
                status: workOrder.completionStatus.toLowerCase(),
                poNumber: workOrder.poNumber || null,
                soNumber: workOrder.soNumber || null,
                analysisRequestCount: workOrder?.analysisRequests?.length || 0,
            }));
        },
        sampleHeaders() {
            return [
                'requestNumber',
                'poNumber',
                'type',
                'component',
                'createdAt',
                'status',
                'billable',
                'invoiced',
            ].map(value => ({
                text: this.$t(`analysisRequests.labels.${value}`),
                value,
            }));
        },
    },
    created() {
        this.loading = true;
        EventBus.$on('workOrderTypes', async workOrderTypes => {
            this.queryParams.workOrderTypes = workOrderTypes;
            await this.getBilling();
        });
        this.filters[0].items = this.completionStatus;
        this.loading = false;
        this.initializing = false;
    },
    methods: {
        getInvoicedStatusIcon(status) {
            switch (status) {
                case FULLY_INVOICED:
                    return 'check_circle_outline';
                case PARTIALLY_INVOICED:
                    return 'timelapse';
                case NOTHING_INVOICED:
                    return 'panorama_fish_eye';
                default:
                    return 'mdi-help-circle-outline';
            }
        },
        async getBilling() {
            if (!this.initializing) {
                this.initializing = true;
                const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
                const { data } = await this.$rq.getBillingLab(cleanQueryParams);
                this.workOrders = [...this.workOrders, ...data.workOrders];
                this.pagination = data.pagination;
                this.initializing = false;
            }
        },
        async billingStatusUpdate(id, invoiced) {
            await this.$rq.billingLabStatusUpdate(id, { invoiced });
            await this.fetchQuery();
        },
        getAssignedTo(user) {
            if (!user) {
                return null;
            }
            const { firstName, lastName } = user;
            return `${firstName} ${lastName}`;
        },
        async fetchQuery(queryParams = {}) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                order: this.preparOrderQueryParams(queryParams.order),
                page: 1,
            };
            this.pagination = {};
            this.workOrders = [];
            await this.getBilling();
        },
        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;
            switch (name) {
                case 'clientnumber':
                    newValue = 'clientnumber';
                    break;
                case 'wonumber':
                    newValue = 'contractnumber';
                    break;
                case 'wodate':
                    newValue = 'createdAt';
                    break;
                case 'status':
                    newValue = 'completionstatus';
                    break;
                default:
                    return value;
            }
            return `${newValue}_${order}`;
        },
        info(item) {
            this.$root.$emit('showModal', {
                title: item.woNumber,
                component: Vue.component('AnalysisGroupInfo', AnalysisGroupInfo),
                payload: {
                    billingInfo: item,
                },
                isForm: false,
                onConfirm: requestPaylod => {
                    if (requestPaylod) {
                        this.updateAnalysisRequest(requestPaylod);
                    }
                },
            });
        },
        async updateAnalysisRequest(payload) {
            await this.$rq.updateLabWorkOrder(payload.id, {
                invoiced: payload.invoiced,
                soNumber: payload.soNumber,
            });
            this.fetchQuery();
        },
        isItemCompleted(status) {
            return [this.status.SENT, this.status.APPROVED, this.status.SUBMITTED].includes(status);
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getBilling();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        handleSearch(value) {
            this.queryParams.search = value;
            this.fetchQuery();
        },
        handleApplyFilters(queryParams) {
            this.queryParams = {
                page: 1,
                perpage: 100,
                search: queryParams.reset ? '' : this.queryParams.search,
                completionStatus: queryParams.status.join(','),
                dateFrom: queryParams.startDate ? moment(queryParams.startDate).tz('utc').startOf('day').toISOString() : null,
                dateTo: queryParams.endDate ? moment(queryParams.endDate).tz('utc').endOf('day').toISOString() : null,
                billable: queryParams.billable.includes('billable'),
                notInvoiced: queryParams.processed?.includes(NOTHING_INVOICED) || false,
                partiallyInvoiced: queryParams.processed?.includes(PARTIALLY_INVOICED) || false,
                fullyInvoiced: queryParams.processed?.includes(FULLY_INVOICED) || false,
            };
            this.fetchQuery();
        },
    },
};
</script>

<style lang="scss" scoped>
.custumeTd{
    border-left: 8px solid #26384B !important;
    width: 250px;
}
.w-poNumberBillInvocedType{
    width: 100px;
}
.w-comp{
    width: 270px;
}
.w-createdAt{
    width: 200px;
}
.w-status-{
    width: 130px;
}
</style>
